import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  SendOutlined as SendIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AppDialog from "components/common/AppDialog";
import CopyButton from "components/common/CopyButton";
import {
  SftpExportFilesResponse,
  SftpExportManifest,
  SftpExportStartRequest,
  SftpExportStartResponse,
  useGetJobSftpExportQuery,
  useLazyGetJobSftpExportDownloadUrlQuery,
  useLazyGetJobSftpExportFilesQuery,
  useStartJobSftpExportMutation,
} from "features/adminUsers/adminUsers";
import { pushNotification } from "features/notificationSlice";
import {
  useLazyCheckSftpUsernameQuery,
  useLazyRevealSftpPasswordQuery,
} from "features/sftp/sftp";
import { useAppDispatch } from "features/store";
import useAppNavigate from "hooks/useAppNavigate";
import { usePermissions } from "hooks/usePermissions";
import { formatApiError } from "utils/formatApiError";

export interface SftpExportDialogProps {
  /** The task job the dialog is about; null keeps it closed. */
  jobId: number | null;
  jobName: string;
  canEdit: boolean;
  /** Why the operator may only look (Client View, manager role); null when they may act. */
  readOnlyReason: string | null;
  onClose: () => void;
}

type ReportFilter = "all" | "copied" | "failed" | "unresolved";

type Credentials = {
  connectionId: number;
  host: string;
  username: string;
  name: string;
  password: string | null;
  created: boolean;
};

const POLL_MS = 2000;
const USERNAME_RE = /^[a-zA-Z0-9._-]+$/;
const LABEL_SX = {
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  fontWeight: 700,
  color: "text.secondary",
};

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 || value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function reportCsv(manifest: SftpExportManifest): string {
  const rows: string[][] = [
    ["employee", "employee_id", "source", "destination", "result", "reasons", "error"],
  ];
  for (const entry of manifest.files ?? []) {
    rows.push([
      entry.employee_name,
      entry.employee_id ?? "",
      entry.source_key,
      entry.dest_key,
      entry.status,
      entry.reasons.join("; "),
      entry.error ?? "",
    ]);
  }
  for (const item of manifest.unresolved ?? []) {
    rows.push([
      item.employee_name ?? "",
      item.employee_id ?? "",
      item.filename,
      "",
      "unresolved",
      item.reason,
      "",
    ]);
  }
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

function relativeDestination(entry: { dest_key: string }, destPrefix: string): string {
  return entry.dest_key.startsWith(destPrefix)
    ? entry.dest_key.slice(destPrefix.length)
    : entry.dest_key;
}

function errorPayload(error: unknown): Record<string, unknown> | undefined {
  const data = (error as { data?: unknown } | undefined)?.data;
  return data && typeof data === "object" ? (data as Record<string, unknown>) : undefined;
}

/** A read-only value with its copy button, the SFTP page's Quick Copy row. */
function CredentialRow({
  label,
  value,
  mono = true,
  secret = false,
  shown = true,
  onToggle,
}: {
  label: string;
  value: string;
  mono?: boolean;
  secret?: boolean;
  shown?: boolean;
  onToggle?: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ width: 84, flexShrink: 0, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: mono ? "monospace" : undefined,
          fontSize: 13,
          overflowWrap: "anywhere",
        }}
      >
        {secret && !shown ? "•".repeat(12) : value}
      </Typography>
      {secret && onToggle && (
        <Tooltip title={shown ? "Hide password" : "Show password"} disableInteractive>
          <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }} aria-label="Toggle password">
            {shown ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      )}
      <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />
    </Stack>
  );
}

/**
 * "Send rejected files to SFTP" for one upload job: what the file logs say the
 * vendor refused, the SFTP connection that hands it to the client (reused or
 * created with a generated password), the copy's progress, and afterwards the
 * report and the files themselves. The copy runs on the server; while it does,
 * the dialog polls the export status, and closing it changes nothing.
 */
export default function SftpExportDialog({
  jobId,
  jobName,
  canEdit,
  readOnlyReason,
  onClose,
}: SftpExportDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { seesSurface } = usePermissions();
  const open = jobId != null;
  const queryJobId = jobId ?? 0;

  // Two reads of one endpoint: a light one polled while the copy runs, and
  // the full one (preview, file list) fetched whenever nothing is running.
  const light = useGetJobSftpExportQuery(
    { jobId: queryJobId },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  // currentData, not data: RTK Query's `data` is the last result of this hook
  // whatever the argument, so it would keep showing job A while job B loads.
  const lightData = light.currentData;
  const running = lightData?.export?.state === "running";
  const full = useGetJobSftpExportQuery(
    { jobId: queryJobId, preview: true, includeFiles: true },
    { skip: !open || running, refetchOnMountOrArgChange: true },
  );
  // The preview reads the job's file logs; when that call fails the light status
  // is still there, and the failure has to be said out loud, not shown as an
  // empty card with a greyed-out button.
  const previewError = !running && !full.isFetching && full.error ? full.error : undefined;
  useEffect(() => {
    if (!open || !running) return undefined;
    const timer = window.setInterval(() => {
      void light.refetch();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, running, light]);

  const fullData = full.currentData;
  const status = fullData ?? lightData;
  const manifest: SftpExportManifest | null =
    (running ? lightData?.export : fullData?.export ?? lightData?.export) ?? null;
  const eligibility = status?.eligibility ?? null;
  const preview = fullData?.preview ?? null;
  const sftp = status?.sftp ?? null;
  const previousState = manifest?.state ?? "none";

  const [startExport, { isLoading: starting }] = useStartJobSftpExportMutation();
  const [checkUsername] = useLazyCheckSftpUsernameQuery();
  const [revealPassword, { isFetching: revealing }] = useLazyRevealSftpPasswordQuery();
  const [fetchFiles] = useLazyGetJobSftpExportFilesQuery();
  const [fetchDownloadUrl] = useLazyGetJobSftpExportDownloadUrlQuery();

  const [mode, setMode] = useState<"reuse" | "create">("reuse");
  const [connectionId, setConnectionId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [seededFor, setSeededFor] = useState<number | null>(null);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [passwordShown, setPasswordShown] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [tab, setTab] = useState<"report" | "files">("report");
  const [reportFilter, setReportFilter] = useState<ReportFilter>("all");
  const [files, setFiles] = useState<SftpExportFilesResponse | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const usernameTimer = useRef<number | null>(null);
  const wasRunningRef = useRef(false);

  // A fresh dialog per job: everything the previous job left behind is
  // dropped the moment another job (or none) is shown.
  useEffect(() => {
    setSeededFor(null);
    setCredentials(null);
    setPasswordShown(false);
    setStartError(null);
    setTab("report");
    setReportFilter("all");
    setFiles(null);
    setFilesError(null);
    setUsernameError("");
    wasRunningRef.current = false;
  }, [jobId]);

  // The form is seeded from the backend's proposal once per job - from this
  // job's answer, never from the one still on screen from the job before.
  useEffect(() => {
    if (!open || !sftp || seededFor === jobId) return;
    setMode(sftp.existing.length ? "reuse" : "create");
    setConnectionId(sftp.existing[0]?.id ?? null);
    setName(sftp.proposed.name);
    setUsername(
      sftp.proposed.username_available
        ? sftp.proposed.username
        : sftp.proposed.suggested_username,
    );
    setSeededFor(jobId);
  }, [open, jobId, sftp, seededFor]);

  // One toast when the copy the dialog watched comes to an end.
  useEffect(() => {
    if (running) {
      wasRunningRef.current = true;
      return;
    }
    if (!wasRunningRef.current || !manifest || manifest.state === "running") return;
    wasRunningRef.current = false;
    const { copied, failed } = manifest.counts;
    dispatch(
      pushNotification({
        severity: failed > 0 || manifest.state === "failed" ? "warning" : "success",
        message:
          manifest.state === "failed"
            ? `Rejected files export failed: ${manifest.error ?? "unknown error"}.`
            : `Rejected files sent to SFTP: ${copied} copied, ${failed} failed.`,
      }),
    );
  }, [running, manifest, dispatch]);

  const loadFiles = useCallback(async () => {
    if (jobId == null) return;
    setFilesLoading(true);
    setFilesError(null);
    try {
      setFiles(await fetchFiles({ jobId, scope: "job" }).unwrap());
    } catch (error) {
      setFilesError(formatApiError(errorPayload(error), "The files could not be listed."));
    } finally {
      setFilesLoading(false);
    }
  }, [jobId, fetchFiles]);

  useEffect(() => {
    if (open && tab === "files" && !running && files == null && !filesLoading) {
      void loadFiles();
    }
  }, [open, tab, running, files, filesLoading, loadFiles]);

  const validateUsername = (value: string) => {
    const trimmed = value.trim();
    if (usernameTimer.current) window.clearTimeout(usernameTimer.current);
    if (!trimmed) {
      setUsernameError("Username is required.");
      return;
    }
    if (trimmed.length < 3) {
      setUsernameError("Username must be at least 3 characters.");
      return;
    }
    if (!USERNAME_RE.test(trimmed)) {
      setUsernameError("Letters, digits, dots, dashes and underscores only.");
      return;
    }
    setUsernameError("");
    usernameTimer.current = window.setTimeout(async () => {
      try {
        const result = await checkUsername({
          username: trimmed,
          server_id: sftp?.server_id,
        }).unwrap();
        setUsernameError(result.exists ? "Username already taken." : "");
      } catch {
        setUsernameError("");
      }
    }, 400);
  };

  const handleStart = async () => {
    if (jobId == null) return;
    setStartError(null);
    const request: SftpExportStartRequest =
      mode === "reuse" && connectionId != null
        ? { mode: "reuse", connection_id: connectionId }
        : { mode: "create", username: username.trim(), name: name.trim() };
    try {
      const result: SftpExportStartResponse = await startExport({ jobId, sftp: request }).unwrap();
      setCredentials({
        connectionId: result.sftp.id,
        host: result.sftp.host,
        username: result.sftp.username,
        name: result.sftp.name,
        password: result.sftp.password,
        created: result.sftp.created,
      });
      setPasswordShown(true);
      setFiles(null);
      setTab("report");
      // The next run reuses what this one just created.
      setMode("reuse");
      setConnectionId(result.sftp.id);
      wasRunningRef.current = true;
      await light.refetch();
    } catch (error) {
      const data = errorPayload(error);
      const code = typeof data?.code === "string" ? data.code : "";
      if (code === "export_running") {
        void light.refetch();
        return;
      }
      if (code === "sftp_username_taken" && typeof data?.suggested_username === "string") {
        setUsernameError(`Username already taken - try ${data.suggested_username}.`);
      }
      const nested = data?.errors;
      const message =
        typeof data?.message === "string"
          ? nested
            ? `${data.message} ${formatApiError(nested, "")}`
            : data.message
          : formatApiError(data, "The export could not be started.");
      setStartError(message);
      dispatch(pushNotification({ severity: "error", message }));
    }
  };

  const handleReveal = async () => {
    const id = credentials?.connectionId ?? manifest?.sftp_connection_id ?? null;
    if (id == null) return;
    try {
      const result = await revealPassword(id).unwrap();
      setCredentials((current) => ({
        connectionId: id,
        host: current?.host ?? sftp?.host ?? "",
        username: current?.username ?? shownConnection?.username ?? "",
        name: current?.name ?? shownConnection?.name ?? "",
        password: result.password,
        created: current?.created ?? false,
      }));
      setPasswordShown(true);
    } catch (error) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(errorPayload(error), "The password could not be revealed."),
        }),
      );
    }
  };

  const handleDownloadReport = () => {
    if (!manifest) return;
    const blob = new Blob([reportCsv(manifest)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    const safe = (jobName || `job-${manifest.job_id}`).replace(/[^A-Za-z0-9._-]+/g, "_");
    anchor.download = `${safe}_rejected_files_export.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFile = async (key: string) => {
    if (jobId == null) return;
    setDownloadingKey(key);
    try {
      const { url } = await fetchDownloadUrl({ jobId, key }).unwrap();
      window.open(url, "_blank", "noopener");
    } catch (error) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(errorPayload(error), "The file could not be downloaded."),
        }),
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  // The connection behind the last export, as the status endpoint lists it.
  const shownConnection = useMemo(() => {
    const id = credentials?.connectionId ?? manifest?.sftp_connection_id ?? null;
    if (id == null || !sftp) return null;
    return [...sftp.existing, ...sftp.inactive].find((c) => c.id === id) ?? null;
  }, [credentials, manifest, sftp]);
  const credentialHost = credentials?.host ?? sftp?.host ?? "";
  const credentialUsername = credentials?.username ?? shownConnection?.username ?? "";
  const credentialConnectionId = credentials?.connectionId ?? manifest?.sftp_connection_id ?? null;
  const showCredentials = credentialConnectionId != null && (credentials != null || manifest != null);

  const handleCopyAll = () => {
    const lines = [`Host: ${credentialHost}`, `Username: ${credentialUsername}`];
    if (credentials?.password) lines.push(`Password: ${credentials.password}`);
    navigator.clipboard?.writeText(lines.join("\n"));
    dispatch(pushNotification({ severity: "success", message: "Connection info copied." }));
  };

  const entries = useMemo(() => manifest?.files ?? [], [manifest]);
  const filteredEntries = useMemo(() => {
    if (reportFilter === "unresolved") return [];
    return entries.filter((entry) => reportFilter === "all" || entry.status === reportFilter);
  }, [entries, reportFilter]);
  const unresolved = manifest?.unresolved ?? [];
  const destPrefix = manifest?.dest_prefix ?? status?.dest_prefix ?? "";

  const canStart =
    canEdit &&
    !!eligibility?.ok &&
    !!preview &&
    preview.entries > 0 &&
    !running &&
    !starting &&
    (mode === "reuse" ? connectionId != null : !usernameError && username.trim().length >= 3 && name.trim().length > 0);
  const startDisabledReason = readOnlyReason
    ? readOnlyReason
    : eligibility && !eligibility.ok
      ? eligibility.reason
      : previewError
        ? "The job's file logs could not be read - retry above."
        : preview && preview.entries === 0
          ? "The file logs name no rejected file that could be located."
          : running
            ? "An export is running."
            : "";
  const startLabel =
    previousState === "none"
      ? mode === "create"
        ? "Copy files and create SFTP access"
        : "Copy files"
      : "Run again";

  const loading = open && !status && (light.isFetching || full.isFetching);
  const statusError = light.error ?? (full.error && !running ? full.error : undefined);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseIcon
      title={
        <Stack spacing={0.25}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            Send rejected files to SFTP
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {jobName}
          </Typography>
        </Stack>
      }
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Tooltip title={canStart ? "" : startDisabledReason}>
            <span>
              <Button
                variant="contained"
                startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={!canStart}
                onClick={() => void handleStart()}
              >
                {starting ? "Starting..." : startLabel}
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <Stack spacing={2} sx={{ pb: 1 }}>
        {loading && (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={120} />
          </Stack>
        )}
        {statusError && !status && (
          <Alert severity="error">
            {formatApiError(errorPayload(statusError), "The export status could not be read.")}
          </Alert>
        )}

        {status && previewError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void full.refetch()}>
                Retry
              </Button>
            }
          >
            Could not read the job's file logs:{" "}
            {formatApiError(errorPayload(previewError), "the server returned an error.")}
          </Alert>
        )}

        {status && !running && eligibility && !eligibility.ok && (
          <Alert severity={previousState === "none" ? "warning" : "info"}>
            {eligibility.reason}
            {previousState !== "none" && " The earlier export is shown below."}
          </Alert>
        )}

        {/* What will be copied */}
        {status && !running && eligibility?.ok && !previewError && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={LABEL_SX}>
              Rejected files
            </Typography>
            {full.isFetching && !preview ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Reading the job's file logs...
              </Typography>
            ) : preview ? (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="body2">
                  <strong>{preview.rejected_files}</strong> file(s) of{" "}
                  <strong>{preview.employees}</strong> employee(s) could not be uploaded
                  {preview.mode === "zip"
                    ? ` - each employee's folder is archived again into ${preview.entries} zip(s).`
                    : "."}
                  {preview.unresolved.length > 0 &&
                    ` ${preview.unresolved.length} file(s) name an employee whose folder could not be found and are left out.`}
                  {preview.error_employees_without_files > 0 &&
                    ` ${preview.error_employees_without_files} employee(s) in ERROR have no rejected file on record.`}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {Object.entries(preview.reasons)
                    .slice(0, 6)
                    .map(([reason, count]) => (
                      <Chip key={reason} size="small" variant="outlined" label={`${reason}: ${count}`} />
                    ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                  Destination: {status.dest_prefix}
                </Typography>
              </Stack>
            ) : null}
          </Paper>
        )}

        {/* Progress */}
        {running && manifest && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={LABEL_SX}>
              Copying
            </Typography>
            <LinearProgress variant="determinate" value={manifest.progress_pct} sx={{ my: 1 }} />
            <Typography variant="body2">
              {manifest.counts.done === 0
                ? "Queued on the server - waiting for the worker to pick it up..."
                : `${manifest.counts.done} of ${manifest.counts.planned} ${
                    manifest.mode === "zip" ? "archive(s)" : "file(s)"
                  }${manifest.counts.failed > 0 ? ` - ${manifest.counts.failed} failed so far` : ""}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You can close this window - the export keeps running on the server.
            </Typography>
          </Paper>
        )}

        {/* Result */}
        {!running && manifest && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
              <Typography variant="caption" sx={LABEL_SX}>
                Last export
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                <Chip size="small" color="success" variant="outlined" label={`${manifest.counts.copied} copied`} />
                {manifest.counts.failed > 0 && (
                  <Chip size="small" color="error" variant="outlined" label={`${manifest.counts.failed} failed`} />
                )}
                {manifest.counts.unresolved > 0 && (
                  <Chip size="small" color="warning" variant="outlined" label={`${manifest.counts.unresolved} unresolved`} />
                )}
                <Chip size="small" variant="outlined" label={formatBytes(manifest.counts.bytes)} />
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {manifest.state === "interrupted"
                ? `Started ${formatWhen(manifest.started_at)} and stopped reporting - the server restarted or the copy died. Run it again; files already copied are simply rewritten.`
                : manifest.state === "failed"
                  ? `Failed ${formatWhen(manifest.finished_at)}: ${manifest.error ?? "unknown error"}.`
                  : `Finished ${formatWhen(manifest.finished_at)}${manifest.started_by?.username ? ` by ${manifest.started_by.username}` : ""}.`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              {manifest.dest_prefix}
            </Typography>
          </Paper>
        )}

        {/* Credentials */}
        {showCredentials && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "calibrate.primarySoft04" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="caption" sx={LABEL_SX}>
                SFTP access for the client
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Copy host, username and password" disableInteractive>
                  <IconButton size="small" onClick={handleCopyAll} aria-label="Copy connection info">
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {seesSurface("sftp_management") && (
                  <Tooltip title="Open in SFTP Management" disableInteractive>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/sftp-management/${credentialConnectionId}`)}
                      aria-label="Open in SFTP Management"
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              <CredentialRow label="Host" value={credentialHost} />
              <CredentialRow label="Username" value={credentialUsername} />
              {credentials?.password ? (
                <CredentialRow
                  label="Password"
                  value={credentials.password}
                  secret
                  shown={passwordShown}
                  onToggle={() => setPasswordShown((v) => !v)}
                />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ width: 84, flexShrink: 0, color: "text.secondary" }}>
                    Password
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={revealing ? <CircularProgress size={12} /> : <Visibility sx={{ fontSize: 16 }} />}
                    disabled={revealing}
                    onClick={() => void handleReveal()}
                  >
                    Reveal stored password
                  </Button>
                </Stack>
              )}
              {credentials?.name && (
                <Typography variant="caption" color="text.secondary">
                  {credentials.created ? "New connection" : "Existing connection"} "{credentials.name}",
                  read-only, mounted at {sftp?.root_prefix ?? ""}.
                </Typography>
              )}
            </Stack>
            {credentials?.created && credentials.password && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Copy the password now and hand it to the client over a secure channel. It can be
                revealed again later in SFTP Management.
              </Alert>
            )}
          </Paper>
        )}

        {/* SFTP set-up for the next run */}
        {status && !running && sftp && eligibility?.ok && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={LABEL_SX}>
              SFTP connection
            </Typography>
            {sftp.existing.length === 0 && sftp.inactive.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                An inactive connection ({sftp.inactive.map((c) => c.username).join(", ")}) already
                mounts this folder. Activate it in SFTP Management to reuse it, or create a new one.
              </Alert>
            )}
            <RadioGroup
              value={mode}
              onChange={(event) => setMode(event.target.value as "reuse" | "create")}
              sx={{ mt: 0.5 }}
            >
              {sftp.existing.map((connection) => (
                <FormControlLabel
                  key={connection.id}
                  value="reuse"
                  control={
                    <Radio
                      size="small"
                      checked={mode === "reuse" && connectionId === connection.id}
                      onChange={() => {
                        setMode("reuse");
                        setConnectionId(connection.id);
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Use existing connection <strong>{connection.name}</strong> (user{" "}
                      <code>{connection.username}</code>) - the client already sees this folder
                      {connection.label ? ` as "${connection.label}"` : ""}.
                    </Typography>
                  }
                />
              ))}
              <FormControlLabel
                value="create"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    Create a new read-only connection with a generated password
                  </Typography>
                }
              />
            </RadioGroup>
            {mode === "create" && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Connection name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={!canEdit}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="SFTP username"
                  value={username}
                  error={!!usernameError}
                  helperText={usernameError || "Prefilled with the backend user's name."}
                  onChange={(event) => {
                    setUsername(event.target.value);
                    validateUsername(event.target.value);
                  }}
                  disabled={!canEdit}
                  inputProps={{ style: { fontFamily: "monospace" } }}
                />
              </Stack>
            )}
          </Paper>
        )}

        {startError && <Alert severity="error">{startError}</Alert>}

        {/* Report / Files */}
        {!running && manifest && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Tabs
                value={tab}
                onChange={(_event, value: "report" | "files") => setTab(value)}
                sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40, flex: 1 }}
              >
                <Tab value="report" label="Report" sx={{ minHeight: 40, textTransform: "none" }} />
                <Tab value="files" label="Files in the folder" sx={{ minHeight: 40, textTransform: "none" }} />
              </Tabs>
              {tab === "report" ? (
                <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadReport}>
                  Download report (CSV)
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={filesLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                  disabled={filesLoading}
                  onClick={() => void loadFiles()}
                >
                  Refresh
                </Button>
              )}
            </Stack>

            {tab === "report" && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {(
                    [
                      ["all", `All (${entries.length})`],
                      ["copied", `Copied (${manifest.counts.copied})`],
                      ["failed", `Failed (${manifest.counts.failed})`],
                      ["unresolved", `Unresolved (${unresolved.length})`],
                    ] as [ReportFilter, string][]
                  ).map(([value, label]) => (
                    <Chip
                      key={value}
                      size="small"
                      label={label}
                      color={reportFilter === value ? "primary" : "default"}
                      variant={reportFilter === value ? "filled" : "outlined"}
                      onClick={() => setReportFilter(value)}
                    />
                  ))}
                </Stack>
                {full.isFetching && entries.length === 0 ? (
                  <Skeleton variant="rounded" height={80} />
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportFilter !== "unresolved" &&
                          filteredEntries.map((entry) => (
                            <TableRow key={entry.dest_key} hover>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {entry.employee_name}
                                {entry.employee_id && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {" "}({entry.employee_id})
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere" }}>
                                {relativeDestination(entry, destPrefix)}
                                {entry.records > 1 && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {" "}({entry.records} records)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{entry.reasons.join("; ")}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={entry.status === "copied" ? "success" : entry.status === "failed" ? "error" : "default"}
                                  label={entry.status}
                                />
                                {entry.error && (
                                  <Typography variant="caption" color="error" display="block" sx={{ overflowWrap: "anywhere" }}>
                                    {entry.error}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        {(reportFilter === "unresolved" || reportFilter === "all") &&
                          unresolved.map((item) => (
                            <TableRow key={`u-${item.filename}-${item.employee_name}`} hover>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{item.employee_name ?? "-"}</TableCell>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{item.filename}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{item.reason}</TableCell>
                              <TableCell>
                                <Chip size="small" variant="outlined" color="warning" label="unresolved" />
                              </TableCell>
                            </TableRow>
                          ))}
                        {filteredEntries.length === 0 &&
                          (reportFilter !== "all" && reportFilter !== "unresolved" ? true : unresolved.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Typography variant="body2" color="text.secondary">
                                  Nothing here.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Stack>
            )}

            {tab === "files" && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {filesError && <Alert severity="error">{filesError}</Alert>}
                {filesLoading && !files && <Skeleton variant="rounded" height={80} />}
                {files && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {files.total_files} file(s), {formatBytes(files.total_bytes)} under{" "}
                      <span style={{ fontFamily: "monospace" }}>{files.dest_prefix}</span>
                      {files.truncated && " (listing cut short)"}
                    </Typography>
                    {files.total_files === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        The folder is empty.
                      </Typography>
                    )}
                    {files.groups.map((group) =>
                      group.employees.map((employee) => (
                        <Box key={`${group.document_type}/${employee.folder}`}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {employee.folder || group.document_type}
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Stack spacing={0.25}>
                            {employee.files.map((file) => (
                              <Stack key={file.key} direction="row" spacing={1} alignItems="center" sx={{ pl: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ flex: 1, minWidth: 0, fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere" }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                  {formatBytes(file.size)}
                                </Typography>
                                <Tooltip title="Download" disableInteractive>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={downloadingKey === file.key}
                                      onClick={() => void handleDownloadFile(file.key)}
                                      aria-label={`Download ${file.name}`}
                                    >
                                      {downloadingKey === file.key ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <DownloadIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      )),
                    )}
                  </>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </AppDialog>
  );
}
